import React, { useEffect, useMemo, useState } from "react";
import { apiService, type Toy } from "../services/api";
import { Section, Container, Heading, Input, Button } from "toddlar-ui";
import SkeletonCard from "../components/SkeletonCard";
import ToyCard from "../components/ToyCard";

const Browse: React.FC = () => {
  const [toys, setToys] = useState<Toy[]>([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("");
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError("");
        const [cats, list] = await Promise.all([
          apiService.getCategories().catch(() => ({ success: false } as any)),
          apiService
            .getToys({ inStock: true, limit: 100 })
            .catch(() => ({ success: false } as any)),
        ]);
        if (cats.success && cats.data) setCategories(cats.data.categories);
        if (list.success && list.data) setToys(list.data.toys);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load toys");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return toys.filter((t) => {
      const matchesQuery =
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q);
      const matchesCat = !category || t.category === category;
      return matchesQuery && matchesCat;
    });
  }, [toys, search, category]);

  return (
    <div className="min-h-screen bg-sand-50">
      <Section background="sand">
        <Container>
          <Heading size="display" className="text-ink">
            Browse Toys
          </Heading>

          {/* Filters */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 justify-center items-center">
            <div className="md:col-span-2 flex gap-3">
              <Input
                placeholder="Search by name or description"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="large"
                className="flex-1"
                aria-label="Search toys"
              />
              <select
                className="rounded-lg border border-sand-300 bg-white text-ink h-12 px-3
                           focus:outline-none focus:ring-2 focus:ring-brand-600"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                aria-label="Filter by category"
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <Button
              className="w-24 ml-4"
              onClick={() => {
                setSearch("");
                setCategory("");
              }}
            >
              Clear
            </Button>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          {loading ? (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              role="status"
              aria-label="Loading toys"
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-800">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-sand-200 bg-white p-10 text-center">
              <p className="text-ink font-semibold">No toys found</p>
              <p className="mt-1 text-ink-muted text-sm">
                Try clearing filters or searching for something else.
              </p>
              <div className="mt-4">
                <Button
                  onClick={() => {
                    setSearch("");
                    setCategory("");
                  }}
                >
                  Clear filters
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((toy) => (
                <ToyCard key={toy._id} toy={toy} />
              ))}
            </div>
          )}
        </Container>
      </Section>
    </div>
  );
};

export default Browse;
